import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button } from "../Button";
import {
  type CertificateNameLayout,
  DEFAULT_CERTIFICATE_NAME_LAYOUT,
} from "../../types/certificate-name-layout.interface";

type Props = {
  value: CertificateNameLayout;
  onChange: (next: CertificateNameLayout) => void;
};

type LayoutKey = keyof CertificateNameLayout;

const FIELDS: {
  key: LayoutKey;
  label: string;
  min: number;
  max: number;
}[] = [
  { key: "topPercent", label: "Topo (%)", min: 0, max: 100 },
  { key: "leftPercent", label: "Esquerda (%)", min: 0, max: 100 },
  { key: "translateXPercent", label: "Ajuste horizontal (%)", min: -100, max: 100 },
  { key: "translateYPercent", label: "Ajuste vertical (%)", min: -100, max: 100 },
  { key: "fontSizeRem", label: "Tamanho da fonte (rem)", min: 1, max: 8 },
];

function layoutToStrings(layout: CertificateNameLayout): Record<LayoutKey, string> {
  return {
    topPercent: String(layout.topPercent),
    leftPercent: String(layout.leftPercent),
    translateXPercent: String(layout.translateXPercent),
    translateYPercent: String(layout.translateYPercent),
    fontSizeRem: String(layout.fontSizeRem),
  };
}

function isPartialNumber(raw: string): boolean {
  const trimmed = raw.trim();
  if (trimmed === "" || trimmed === "-" || trimmed === "." || trimmed === "-.") {
    return true;
  }
  return /^-?\d*\.?\d*$/.test(trimmed);
}

function isCompleteNumber(raw: string): boolean {
  const trimmed = raw.trim();
  return (
    trimmed !== "" &&
    trimmed !== "-" &&
    trimmed !== "." &&
    trimmed !== "-." &&
    Number.isFinite(parseFloat(trimmed))
  );
}

function clampNumber(raw: string, fallback: number, min: number, max: number): number {
  if (!isCompleteNumber(raw)) return fallback;
  const v = parseFloat(raw.trim());
  return Math.min(max, Math.max(min, v));
}

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400">
        {label}
      </label>
      {children}
    </div>
  );
}

export function CertificateNamePositionPanel({ value, onChange }: Props) {
  const [drafts, setDrafts] = useState(() => layoutToStrings(value));
  const focusedKey = useRef<LayoutKey | null>(null);

  useEffect(() => {
    if (focusedKey.current === null) {
      setDrafts(layoutToStrings(value));
    }
  }, [value]);

  function patch(p: Partial<CertificateNameLayout>) {
    onChange({ ...value, ...p });
  }

  function handleChange(key: LayoutKey, raw: string) {
    if (!isPartialNumber(raw)) return;

    setDrafts((current) => ({ ...current, [key]: raw }));

    if (!isCompleteNumber(raw)) return;

    const field = FIELDS.find((item) => item.key === key);
    if (!field) return;

    patch({
      [key]: clampNumber(raw, value[key], field.min, field.max),
    });
  }

  function handleBlur(key: LayoutKey) {
    focusedKey.current = null;

    const field = FIELDS.find((item) => item.key === key);
    if (!field) return;

    const parsed = clampNumber(drafts[key], value[key], field.min, field.max);
    patch({ [key]: parsed });
    setDrafts((current) => ({ ...current, [key]: String(parsed) }));
  }

  function handleFocus(key: LayoutKey) {
    focusedKey.current = key;
  }

  function handleRestoreDefault() {
    focusedKey.current = null;
    onChange({ ...DEFAULT_CERTIFICATE_NAME_LAYOUT });
    setDrafts(layoutToStrings(DEFAULT_CERTIFICATE_NAME_LAYOUT));
  }

  const inputClassName =
    "w-full rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100";

  return (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50/80 p-4 dark:border-zinc-700 dark:bg-zinc-900/50">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
            Posição do nome no certificado
          </h3>
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
            Os ajustes valem para{" "}
            <strong>todos</strong> os certificados desta geração (1 ou vários
            participantes). Depois clique em &quot;Baixar certificados&quot;.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          className="shrink-0 text-xs"
          onClick={handleRestoreDefault}
        >
          Restaurar padrão
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {FIELDS.map((field) => (
          <Field key={field.key} label={field.label}>
            <input
              type="text"
              inputMode="decimal"
              value={drafts[field.key]}
              onChange={(e) => handleChange(field.key, e.target.value)}
              onFocus={() => handleFocus(field.key)}
              onBlur={() => handleBlur(field.key)}
              className={inputClassName}
            />
          </Field>
        ))}
      </div>
    </div>
  );
}
